<template>
  <q-page class="legal-doc-page">
    <div class="legal-doc-container">
      <!-- Loading state -->
      <div v-if="loading" class="legal-doc-loading">
        Cargando documento...
      </div>

      <!-- Not found -->
      <div v-else-if="!doc" class="legal-doc-empty">
        <q-icon name="warning" size="32px" style="color: var(--oa-gold-full);" />
        <p class="empty-text">Documento no encontrado</p>
        <router-link :to="{ name: 'legal' }" class="back-link">Volver a documentos</router-link>
      </div>

      <!-- Document loaded -->
      <template v-else>
        <!-- Header -->
        <div class="legal-doc-header">
          <router-link :to="{ name: 'legal' }" class="legal-doc-back">
            <q-icon name="arrow_back" size="20px" />
          </router-link>
          <div>
            <h1 class="legal-doc-title">{{ doc.title }}</h1>
            <p class="legal-doc-version">Version {{ doc.versionNumber }}</p>
          </div>
        </div>

        <!-- Reading progress (sticky) -->
        <div class="legal-doc-progress-bar">
          <div class="legal-doc-progress-info">
            <div class="legal-doc-progress-indicators">
              <span :class="['progress-indicator', { 'indicator-done': hasScrolledAll }]">
                <q-icon name="description" size="14px" />
                {{ hasScrolledAll ? 'Lectura completa' : scrollPct + '% leido' }}
              </span>
              <span :class="['progress-indicator', { 'indicator-done': hasMinReadTime }]">
                <q-icon name="schedule" size="14px" />
                {{ readTimeFormatted }}
                <template v-if="!hasMinReadTime && doc"> / {{ minReadTimeFormatted }}</template>
              </span>
            </div>
            <q-btn
              v-if="canAccept && !showAcceptForm"
              no-caps
              unelevated
              size="sm"
              icon="edit"
              label="Firmar documento"
              style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; font-size: 12px;"
              @click="showAcceptForm = true"
            />
          </div>
          <q-linear-progress
            :value="scrollPct / 100"
            :color="hasScrolledAll ? 'green' : 'amber'"
            track-color="grey-9"
            rounded
            size="6px"
          />
        </div>

        <!-- Document content -->
        <div
          ref="contentRef"
          class="legal-doc-content"
          @scroll="handleScroll"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="legal-doc-html" v-html="doc.contentHtml" />
        </div>

        <!-- Acceptance form -->
        <div v-if="showAcceptForm" class="legal-accept-form">
          <div class="legal-accept-header">
            <q-icon name="edit" size="20px" style="color: var(--oa-gold-full);" />
            <h3 class="legal-accept-title">Firma Digital</h3>
          </div>

          <p class="legal-accept-desc">
            Al firmar este documento, declaras que lo has leido en su totalidad y aceptas
            todos los terminos y condiciones establecidos.
          </p>

          <!-- Signature input -->
          <div>
            <label class="legal-accept-label">Escribi tu nombre completo como firma</label>
            <q-input
              v-model="signatureText"
              outlined
              dark
              dense
              placeholder="Nombre y Apellido"
              input-style="font-family: serif; font-style: italic; font-size: 18px;"
              style="margin-top: 6px;"
            />
          </div>

          <!-- Confirmation checkbox -->
          <q-checkbox
            v-model="confirmed"
            dark
            dense
            color="amber"
            class="legal-accept-checkbox"
          >
            <template #default>
              <span class="legal-accept-checkbox-label">
                Declaro que he leido y comprendido la totalidad de este documento, y acepto
                voluntariamente todas las obligaciones y compromisos aqui establecidos.
              </span>
            </template>
          </q-checkbox>

          <!-- Actions -->
          <div class="legal-accept-actions">
            <q-btn
              no-caps
              unelevated
              icon="check_circle"
              :label="submitting ? 'Firmando...' : 'Firmar y Aceptar'"
              :disabled="!signatureText || !confirmed || submitting"
              style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 12px; padding: 8px 24px;"
              @click="handleAccept"
            />
            <q-btn
              no-caps
              flat
              label="Cancelar"
              style="color: var(--oa-text-muted); border-radius: 12px; padding: 8px 16px;"
              @click="showAcceptForm = false"
            />
          </div>

          <p v-if="acceptError" class="legal-accept-error">
            Error al firmar. Intenta de nuevo.
          </p>
        </div>

        <!-- Not ready hint -->
        <div v-if="!canAccept" class="legal-doc-hint">
          <p class="legal-doc-hint-text">
            <template v-if="!hasScrolledAll && !hasMinReadTime">
              Lee el documento completo y espera el tiempo minimo de lectura para poder firmar.
            </template>
            <template v-else-if="!hasScrolledAll">
              Desplazate hasta el final del documento para poder firmar.
            </template>
            <template v-else>
              Espera el tiempo minimo de lectura para poder firmar.
            </template>
          </p>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { api } from 'boot/axios';

// ---------- Types ----------
interface LegalDocument {
  id: string;
  type: string;
  title: string;
  versionId: string;
  versionNumber: number;
  contentHtml: string;
  sections?: unknown[];
  dynamicFields?: unknown;
  minReadTimeSec: number;
}

// ---------- Route ----------
const route = useRoute();
const router = useRouter();
const documentId = computed(() => route.params.documentId as string);

// ---------- State ----------
const loading = ref(true);
const doc = ref<LegalDocument | null>(null);

const scrollPct = ref(0);
const readTimeSec = ref(0);
const hasScrolledAll = ref(false);
const hasMinReadTime = ref(false);
const signatureText = ref('');
const confirmed = ref(false);
const showAcceptForm = ref(false);
const submitting = ref(false);
const acceptError = ref(false);

const contentRef = ref<HTMLElement | null>(null);
let startTime = Date.now();
let readTimeInterval: ReturnType<typeof setInterval> | null = null;

// ---------- Computed ----------
const canAccept = computed(() => hasScrolledAll.value && hasMinReadTime.value);

const readTimeFormatted = computed(() => {
  const mins = Math.floor(readTimeSec.value / 60);
  const secs = readTimeSec.value % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
});

const minReadTimeFormatted = computed(() => {
  if (!doc.value) return '0:00';
  const mins = Math.floor(doc.value.minReadTimeSec / 60);
  const secs = doc.value.minReadTimeSec % 60;
  return `${mins}:${secs.toString().padStart(2, '0')} min`;
});

// ---------- Handlers ----------
function handleScroll() {
  const el = contentRef.value;
  if (!el) return;
  const scrollTop = el.scrollTop;
  const scrollHeight = el.scrollHeight - el.clientHeight;
  if (scrollHeight <= 0) {
    scrollPct.value = 100;
    hasScrolledAll.value = true;
    return;
  }
  const pct = Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
  scrollPct.value = pct;
  if (pct >= 95) hasScrolledAll.value = true;
}

async function handleAccept() {
  if (!doc.value || !signatureText.value || !confirmed.value) return;
  submitting.value = true;
  acceptError.value = false;
  try {
    await api.post(`/legal/${doc.value.id}/accept`, {
      versionId: doc.value.versionId,
      signatureText: signatureText.value,
      readTimeSeconds: readTimeSec.value,
    });
    router.push({ name: 'legal' });
  } catch {
    acceptError.value = true;
  } finally {
    submitting.value = false;
  }
}

// ---------- Data Fetching ----------
async function fetchDocument() {
  loading.value = true;
  try {
    const { data } = await api.get(`/legal/${documentId.value}`);
    doc.value = data;
  } catch {
    doc.value = null;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  startTime = Date.now();
  fetchDocument();

  readTimeInterval = setInterval(() => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    readTimeSec.value = elapsed;
    if (doc.value && elapsed >= doc.value.minReadTimeSec) {
      hasMinReadTime.value = true;
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (readTimeInterval) {
    clearInterval(readTimeInterval);
    readTimeInterval = null;
  }
});
</script>

<style scoped>
.legal-doc-page {
  padding: 24px;
}

.legal-doc-container {
  max-width: 760px;
  margin: 0 auto;
}

.legal-doc-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  font-size: 14px;
  color: var(--oa-text-muted);
}

.legal-doc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 12px;
}

.empty-text {
  font-size: 14px;
  color: var(--oa-text-muted);
}

.back-link {
  font-size: 12px;
  color: var(--oa-gold-full);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

/* Header */
.legal-doc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.legal-doc-back {
  padding: 8px;
  border-radius: 12px;
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: background 0.2s;
}

.legal-doc-back:hover {
  background: var(--oa-bg-3);
}

.legal-doc-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-doc-version {
  font-size: 12px;
  color: var(--oa-text-disabled);
  margin: 0;
}

/* Progress bar */
.legal-doc-progress-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: color-mix(in srgb, var(--oa-bg-2) 90%, transparent);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  border: 1px solid var(--oa-border);
  padding: 12px 16px;
  margin-bottom: 16px;
}

.legal-doc-progress-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.legal-doc-progress-indicators {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--oa-text-disabled);
}

.progress-indicator.indicator-done {
  color: var(--oa-success);
}

/* Document content */
.legal-doc-content {
  background: var(--oa-bg-2);
  border-radius: 16px;
  border: 1px solid var(--oa-border);
  padding: 32px;
  max-height: 60vh;
  overflow-y: auto;
  scroll-behavior: smooth;
  margin-bottom: 16px;
}

.legal-doc-html :deep(h2) {
  font-size: 18px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 24px 0 16px;
}

.legal-doc-html :deep(h2:first-child) {
  margin-top: 0;
}

.legal-doc-html :deep(h3) {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 20px 0 8px;
}

.legal-doc-html :deep(p) {
  font-size: 14px;
  color: var(--oa-text-secondary);
  margin-bottom: 12px;
  line-height: 1.6;
}

.legal-doc-html :deep(ul) {
  font-size: 14px;
  color: var(--oa-text-secondary);
  margin-bottom: 12px;
  padding-left: 20px;
}

.legal-doc-html :deep(li) {
  line-height: 1.6;
  margin-bottom: 4px;
}

.legal-doc-html :deep(strong) {
  color: var(--oa-text-primary);
}

/* Accept form */
.legal-accept-form {
  background: var(--oa-bg-2);
  border-radius: 16px;
  border: 2px solid color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.legal-accept-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legal-accept-title {
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-accept-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

.legal-accept-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-muted);
}

.legal-accept-checkbox {
  margin: 0;
}

.legal-accept-checkbox-label {
  font-size: 12px;
  color: var(--oa-text-muted);
}

.legal-accept-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legal-accept-error {
  font-size: 12px;
  color: #ef4444;
  margin: 0;
}

/* Not ready hint */
.legal-doc-hint {
  background: color-mix(in srgb, var(--oa-bg-2) 50%, transparent);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.legal-doc-hint-text {
  font-size: 12px;
  color: var(--oa-text-disabled);
  margin: 0;
}
</style>
